// Shared utility functions for order period calculations

// Check if order submission is currently allowed - now always true with seamless period transitions
export function isOrderingAllowed(): boolean {
  // Orders are always allowed - the 6am cutoffs just switch delivery periods
  // Monday delivery windows: Thursday 6:01am → Tuesday 6:00am
  // Wednesday delivery windows: Tuesday 6:01am → Thursday 6:00am
  return true;
}

// Get a user-friendly message about when ordering will reopen - no longer needed since ordering is always open
export function getOrderingClosedMessage(): string {
  // Ordering is now always allowed with seamless period transitions
  return "";
}

export function getCurrentPeriod(): string {
  const now = new Date();
  // Convert to EST/Toronto timezone for business logic
  const easternTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Toronto"}));
  
  // Find current delivery window
  // Monday delivery windows: Thursday 6:01am → Tuesday 6:00am
  // Wednesday delivery windows: Tuesday 6:01am → Thursday 6:00am
  
  const currentDeliveryDate = getCurrentDeliveryDate(easternTime);
  const dayName = currentDeliveryDate.getDay() === 1 ? 'Mon' : 'Wed';
  
  return `${(currentDeliveryDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDeliveryDate.getDate().toString().padStart(2, '0')}/${currentDeliveryDate.getFullYear()} (${dayName})`;
}

// Helper function to determine which delivery date the current time falls into
function getCurrentDeliveryDate(easternTime: Date): Date {
  const currentDayOfWeek = easternTime.getDay();
  const currentHour = easternTime.getHours();
  
  // Find the current week's Monday and Wednesday
  // Handle Sunday specially - on Sunday, "this Monday" is tomorrow (add 1 day)
  const startOfWeek = new Date(easternTime);
  if (currentDayOfWeek === 0) {
    // Sunday: tomorrow is Monday
    startOfWeek.setDate(easternTime.getDate() + 1);
  } else {
    // Other days: calculate Monday of current week
    startOfWeek.setDate(easternTime.getDate() - currentDayOfWeek + 1);
  }
  
  const thisMonday = new Date(startOfWeek);
  const thisWednesday = new Date(startOfWeek);
  thisWednesday.setDate(startOfWeek.getDate() + 2); // Wednesday
  
  // Check if we're in the Monday delivery window (Thursday 6:01am → Tuesday 6:00am)
  if (currentDayOfWeek === 4 && currentHour >= 6) {
    // Thursday 6am+ → this week's Monday window starts, delivery is next Monday
    const nextMonday = new Date(thisMonday);
    nextMonday.setDate(thisMonday.getDate() + 7);
    return nextMonday;
  } else if (currentDayOfWeek === 5 || currentDayOfWeek === 6) {
    // Friday, Saturday → next Monday delivery (add 7 days)
    const nextMonday = new Date(thisMonday);
    nextMonday.setDate(thisMonday.getDate() + 7);
    return nextMonday;
  } else if (currentDayOfWeek === 0) {
    // Sunday → tomorrow is Monday, that's our delivery date (already set correctly)
    return thisMonday;
  } else if (currentDayOfWeek === 1 || (currentDayOfWeek === 2 && currentHour < 6)) {
    // Monday all day, Tuesday before 6am → this Monday delivery
    return thisMonday;
  } else if ((currentDayOfWeek === 2 && currentHour >= 6) || currentDayOfWeek === 3 || (currentDayOfWeek === 4 && currentHour < 6)) {
    // Tuesday 6am+, Wednesday all day, Thursday before 6am → this Wednesday delivery
    return thisWednesday;
  }
  
  // Default fallback
  return thisMonday;
}

export function getOrderPeriod(date: Date): string {
  // Convert to EST/Toronto timezone for business logic
  const easternTime = new Date(date.toLocaleString("en-US", {timeZone: "America/Toronto"}));
  
  // Determine which delivery window this order falls into
  const deliveryDate = getOrderDeliveryDate(easternTime);
  const dayName = deliveryDate.getDay() === 1 ? 'Mon' : 'Wed';
  
  return `${(deliveryDate.getMonth() + 1).toString().padStart(2, '0')}/${deliveryDate.getDate().toString().padStart(2, '0')}/${deliveryDate.getFullYear()} (${dayName})`;
}

// Helper function to determine which delivery date an order belongs to
export function getOrderDeliveryDate(orderTime: Date): Date {
  const dayOfWeek = orderTime.getDay();
  const hour = orderTime.getHours();
  
  // We need to find which delivery window this order falls into
  // Monday delivery windows: Thursday 6:01am → Tuesday 6:00am
  // Wednesday delivery windows: Tuesday 6:01am → Thursday 6:00am
  
  // First, find the Monday and Wednesday of the week containing this order
  const mondayOfWeek = new Date(orderTime);
  mondayOfWeek.setDate(orderTime.getDate() - orderTime.getDay() + 1); // Monday of order's week
  
  const wednesdayOfWeek = new Date(mondayOfWeek);
  wednesdayOfWeek.setDate(mondayOfWeek.getDate() + 2); // Wednesday of same week
  
  // Check if this order falls in the Monday delivery window
  // Monday window: Previous Thursday 6:01am → Tuesday 6:00am
  if (dayOfWeek === 1 || (dayOfWeek === 2 && hour < 6)) {
    // Monday all day or Tuesday before 6am → this week's Monday delivery
    return mondayOfWeek;
  } else if (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0) {
    // Friday, Saturday, Sunday → find the upcoming Monday delivery
    // For Sunday, this is typically the Monday immediately following
    const upcomingMonday = new Date(orderTime);
    upcomingMonday.setDate(orderTime.getDate() + (1 - orderTime.getDay() + 7) % 7);
    if (upcomingMonday.getTime() === orderTime.getTime()) {
      upcomingMonday.setDate(upcomingMonday.getDate() + 7);
    }
    return upcomingMonday;
  } else if (dayOfWeek === 4 && hour >= 6) {
    // Thursday 6am+ → start of window for upcoming Monday
    const upcomingMonday = new Date(orderTime);
    upcomingMonday.setDate(orderTime.getDate() + (1 - orderTime.getDay() + 7) % 7);
    if (upcomingMonday.getTime() <= orderTime.getTime()) {
      upcomingMonday.setDate(upcomingMonday.getDate() + 7);
    }
    return upcomingMonday;
  } 
  
  // Check if this order falls in the Wednesday delivery window
  // Wednesday window: Tuesday 6:01am → Thursday 6:00am
  else if ((dayOfWeek === 2 && hour >= 6) || dayOfWeek === 3 || (dayOfWeek === 4 && hour < 6)) {
    // Tuesday 6am+, Wednesday all day, Thursday before 6am → this week's Wednesday delivery
    return wednesdayOfWeek;
  }
  
  // Default fallback (shouldn't happen with proper logic)
  return mondayOfWeek;
}

export function getPreviousPeriod(): string {
  const now = new Date();
  const easternTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Toronto"}));
  const currentDayOfWeek = easternTime.getDay();
  const currentHour = easternTime.getHours();
  
  let previousPeriodDate = new Date(easternTime);
  
  // Find the most recent past period based on Tuesday 6am / Thursday 6am cutoffs
  if (currentDayOfWeek === 0) {
    // Sunday -> previous period is last Wednesday (4 days back)
    previousPeriodDate.setDate(easternTime.getDate() - 4);
  } else if (currentDayOfWeek === 1) {
    // Monday -> current is Monday, previous is last Wednesday (5 days back)
    previousPeriodDate.setDate(easternTime.getDate() - 5);
  } else if (currentDayOfWeek === 2 && currentHour < 6) {
    // Tuesday before 6am -> current is still Monday, previous is last Wednesday (6 days back)
    previousPeriodDate.setDate(easternTime.getDate() - 6);
  } else if (currentDayOfWeek === 2 && currentHour >= 6) {
    // Tuesday 6am+ -> current is Wednesday, previous is Monday (1 day back)
    previousPeriodDate.setDate(easternTime.getDate() - 1);
  } else if (currentDayOfWeek === 3) {
    // Wednesday -> current is Wednesday, previous is Monday (2 days back)
    previousPeriodDate.setDate(easternTime.getDate() - 2);
  } else if (currentDayOfWeek === 4 && currentHour < 6) {
    // Thursday before 6am -> current is still Wednesday, previous is Monday (3 days back)
    previousPeriodDate.setDate(easternTime.getDate() - 3);
  } else if (currentDayOfWeek === 4 && currentHour >= 6) {
    // Thursday 6am+ -> current is next Monday, previous is Wednesday (1 day back)
    previousPeriodDate.setDate(easternTime.getDate() - 1);
  } else if (currentDayOfWeek === 5) {
    // Friday -> current is next Monday, previous is Wednesday (2 days back)
    previousPeriodDate.setDate(easternTime.getDate() - 2);
  } else {
    // Saturday -> current is next Monday, previous is Wednesday (3 days back)
    previousPeriodDate.setDate(easternTime.getDate() - 3);
  }
  
  const prevDayName = previousPeriodDate.getDay() === 1 ? 'Mon' : 'Wed';
  return `${(previousPeriodDate.getMonth() + 1).toString().padStart(2, '0')}/${previousPeriodDate.getDate().toString().padStart(2, '0')}/${previousPeriodDate.getFullYear()} (${prevDayName})`;
}

// Format a period string (MM/DD/YYYY (Day)) into a friendly delivery date format (Sept 11th)
export function formatDeliveryDate(periodString: string): string {
  // Extract date part from "MM/DD/YYYY (Day)" format
  const dateMatch = periodString.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!dateMatch) return periodString;
  
  const [, month, day, year] = dateMatch;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
  ];
  
  const monthName = monthNames[date.getMonth()];
  const dayNum = parseInt(day);
  
  // Add appropriate suffix for day
  let daySuffix;
  if (dayNum >= 11 && dayNum <= 13) {
    daySuffix = 'th';
  } else {
    switch (dayNum % 10) {
      case 1: daySuffix = 'st'; break;
      case 2: daySuffix = 'nd'; break;
      case 3: daySuffix = 'rd'; break;
      default: daySuffix = 'th'; break;
    }
  }
  
  return `${monthName} ${dayNum}${daySuffix}`;
}

// Generate historical periods going back from the previous period
export function getHistoricalPeriods(count: number = 5): string[] {
  const periods: string[] = [];
  
  // Start with the previous period date and work backwards
  const previousPeriod = getPreviousPeriod();
  const previousDateMatch = previousPeriod.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!previousDateMatch) return periods;
  
  const [, month, day, year] = previousDateMatch;
  let currentDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  // Generate periods going backwards
  for (let i = 0; i < count; i++) {
    // Go back to previous delivery period (either 2 days back for Mon->Wed or 5 days back for Wed->Mon)
    if (currentDate.getDay() === 1) {
      // Monday -> go back to previous Wednesday (5 days)
      currentDate.setDate(currentDate.getDate() - 5);
    } else {
      // Wednesday -> go back to previous Monday (2 days)
      currentDate.setDate(currentDate.getDate() - 2);
    }
    
    const dayName = currentDate.getDay() === 1 ? 'Mon' : 'Wed';
    const periodString = `${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${currentDate.getFullYear()} (${dayName})`;
    periods.push(periodString);
  }
  
  return periods;
}

// Get all available periods for dropdown options
export interface PeriodOption {
  value: string;
  label: string;
  isMore?: boolean;
}

export function getAvailablePeriods(): PeriodOption[] {
  const options: PeriodOption[] = [];
  
  // Current period
  const current = getCurrentPeriod();
  options.push({
    value: "current",
    label: `Current Period (${formatDeliveryDate(current)})`
  });
  
  // Previous period
  const previous = getPreviousPeriod();
  options.push({
    value: "previous", 
    label: `Previous Period (${formatDeliveryDate(previous)})`
  });
  
  // Historical periods (5 periods back)
  const historical = getHistoricalPeriods(5);
  historical.forEach((period, index) => {
    options.push({
      value: `historical-${index}`,
      label: formatDeliveryDate(period)
    });
  });
  
  // More option
  options.push({
    value: "more",
    label: "More...",
    isMore: true
  });
  
  return options;
}

// ==========================================
// NEW: Configurable Order Window System
// ==========================================

export type OrderWindowFrequency = 'daily' | 'weekly' | 'biweekly' | 'custom';

export interface OrderWindowConfig {
  enabled: boolean;
  frequency: OrderWindowFrequency;
  timezone: string;
  anchorStart: Date;
  cutoffHour: number;
  cutoffMinute: number;
  customDays: number; // Bitmask: bit 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
}

export interface OrderWindow {
  startTime: Date;
  endTime: Date;
  cutoffTime: Date;
  label: string;
}

// Helper: Get custom days from bitmask as array of day indices (0=Sun, 6=Sat)
export function getCustomDaysFromBitmask(bitmask: number): number[] {
  const days: number[] = [];
  for (let i = 0; i <= 6; i++) {
    if (bitmask & (1 << i)) {
      days.push(i);
    }
  }
  return days.sort((a, b) => a - b);
}

// Helper: Create bitmask from array of day indices
export function createCustomDaysBitmask(days: number[]): number {
  let bitmask = 0;
  for (const day of days) {
    if (day >= 0 && day <= 6) {
      bitmask |= (1 << day);
    }
  }
  return bitmask;
}

// The delivery day of a weekly or biweekly schedule (0=Sun, 6=Sat): the "Delivery Day"
// picked in Settings, stored as its bit in customDays. Monday when none is set. A
// bitmask with several bits (left over from a custom schedule) reads as its highest,
// which is what Settings shows.
export function weeklyDeliveryDay(customDays: number): number {
  return customDays > 0 ? Math.floor(Math.log2(customDays)) : 1;
}

// The weekdays an org delivers on, as a bitmask (bit 0=Sun … bit 6=Sat).
// customDays means something different per frequency: daily ignores it, weekly and
// biweekly store their one delivery day in it, and only custom stores the day set.
// A biweekly schedule delivers on its day every week: a weekday mask cannot say
// "every other".
export function deliveryDaysMask(config: { frequency?: string | null; customDays?: number | null }): number {
  const customDays = config.customDays ?? 0;
  switch (config.frequency) {
    case 'daily':
      return 0b1111111;
    case 'custom':
      // No days chosen: the windows fall back to weekly (calculateCustomWindows).
      if (customDays & 0b1111111) return customDays & 0b1111111;
      return 1 << weeklyDeliveryDay(0);
    default:
      // weekly, biweekly, and anything unrecognised (calculateOrderWindows treats that as weekly)
      return 1 << weeklyDeliveryDay(customDays);
  }
}

// The weekday (0=Sun, 6=Sat) of a calendar date "YYYY-MM-DD", or null when the text
// is not a real date. A fulfillment date is a calendar date, not an instant: its
// weekday is the same in every zone, so it is read at noon UTC and never through the
// process's clock.
export function weekdayOfCalendarDate(ymd: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  // 2026-02-30 would roll over into March: not a date.
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return date.getUTCDay();
}

// Get day names for display
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function getDayName(dayIndex: number, full: boolean = false): string {
  return full ? DAY_NAMES_FULL[dayIndex] : DAY_NAMES[dayIndex];
}

// Format time for display (e.g., "5:00 PM")
export function formatCutoffTime(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
}

// ------------------------------------------------------------------
// Wall-clock time in a named zone
//
// A cutoff is a time on the org's wall clock ("06:00 in Toronto"), and a window
// boundary is the INSTANT that clock shows it. Nothing below reads the process
// time zone: production runs in UTC, a browser runs wherever its user is, and
// both must name the same instant.
//
// A wall-clock reading is carried as a Date whose UTC fields hold the reading
// (06:00 on the wall is 06:00Z in the value). That scale has no daylight saving,
// so calendar arithmetic on it is exact. It is NOT an instant — only
// `wallClockToInstant` turns it into one.
// ------------------------------------------------------------------

const zoneClocks = new Map<string, Intl.DateTimeFormat>();

function zoneClock(timezone: string): Intl.DateTimeFormat {
  let clock = zoneClocks.get(timezone);
  if (!clock) {
    clock = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    zoneClocks.set(timezone, clock);
  }
  return clock;
}

const SECOND_MS = 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

// What the wall clock in `timezone` shows at an instant, to the second.
function wallClockAtMs(instantMs: number, timezone: string): number {
  const fields: Record<string, number> = {};
  for (const part of zoneClock(timezone).formatToParts(instantMs)) {
    if (part.type !== "literal") fields[part.type] = parseInt(part.value, 10);
  }
  return Date.UTC(fields.year, fields.month - 1, fields.day, fields.hour % 24, fields.minute, fields.second);
}

// The wall-clock reading in `timezone` at an instant, held in the UTC fields of
// the result. For calendar comparisons and display — it is not an instant.
export function wallClockInTimezone(instant: Date, timezone: string): Date {
  return new Date(wallClockAtMs(instant.getTime(), timezone));
}

// The calendar date (YYYY-MM-DD) in `timezone` at an instant.
export function calendarDateInTimezone(instant: Date, timezone: string): string {
  return wallClockInTimezone(instant, timezone).toISOString().split("T")[0];
}

// The instant at which the wall clock in `timezone` shows `wall`.
//
// Twice a year the answer is not unique. A reading the clock skips (02:30 on the
// night the clocks go forward) resolves to the same time after the jump, 03:30;
// a reading it shows twice (01:30 on the night they go back) resolves to the
// first. Both match what a Date does in its own local zone.
export function wallClockToInstant(wall: Date, timezone: string): Date {
  const ms = ((wall.getTime() % SECOND_MS) + SECOND_MS) % SECOND_MS;
  const reading = wall.getTime() - ms;
  const offsetAt = (instantMs: number) => wallClockAtMs(instantMs, timezone) - instantMs;

  // The zone's offset a day either side: equal unless a change falls in between.
  const before = offsetAt(reading - DAY_MS);
  const after = offsetAt(reading + DAY_MS);
  if (before === after) return new Date(reading - before + ms);

  const candidates = [reading - before, reading - after]
    .filter((candidate) => wallClockAtMs(candidate, timezone) === reading);
  const instantMs = candidates.length > 0 ? Math.min(...candidates) : reading - before;
  return new Date(instantMs + ms);
}

// A cutoff: the reading on the org's wall clock, and the instant it is shown.
interface Cutoff {
  wall: Date;
  instant: Date;
}

type OrderWindows = { current: OrderWindow; previous: OrderWindow; next: OrderWindow };

// Calculate window boundaries based on config. `referenceDate` is an instant.
export function calculateOrderWindows(config: OrderWindowConfig, referenceDate?: Date): OrderWindows {
  const now = referenceDate || new Date();
  
  switch (config.frequency) {
    case 'daily':
      return calculateDailyWindows(config, now);
    case 'weekly':
      return calculateWeeklyWindows(config, now);
    case 'biweekly':
      return calculateBiweeklyWindows(config, now);
    case 'custom':
      return calculateCustomWindows(config, now);
    default:
      return calculateWeeklyWindows(config, now);
  }
}

// Everything the window maths needs to know about "now" in the org's zone.
function orgCalendar(config: OrderWindowConfig, now: Date) {
  const { timezone, cutoffHour, cutoffMinute } = config;
  const today = wallClockInTimezone(now, timezone);

  // The cutoff on the org's calendar day `dayOffset` days from today.
  const cutoffs = new Map<number, Cutoff>();
  const cutoffOn = (dayOffset: number): Cutoff => {
    let cutoff = cutoffs.get(dayOffset);
    if (!cutoff) {
      const wall = new Date(Date.UTC(
        today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + dayOffset,
        cutoffHour, cutoffMinute, 0, 0,
      ));
      cutoff = { wall, instant: wallClockToInstant(wall, timezone) };
      cutoffs.set(dayOffset, cutoff);
    }
    return cutoff;
  };

  return {
    today,
    dayOfWeek: today.getUTCDay(),
    cutoffOn,
    beforeTodaysCutoff: now.getTime() < cutoffOn(0).instant.getTime(),
    // Three consecutive windows of `length` days, the middle one starting `start` days from today.
    windowsFrom: (start: number, length: number): OrderWindows => ({
      current: createWindow(cutoffOn(start), cutoffOn(start + length), config),
      previous: createWindow(cutoffOn(start - length), cutoffOn(start), config),
      next: createWindow(cutoffOn(start + length), cutoffOn(start + 2 * length), config),
    }),
  };
}

// Daily windows: cutoff to cutoff, one calendar day apart
function calculateDailyWindows(config: OrderWindowConfig, now: Date): OrderWindows {
  const calendar = orgCalendar(config, now);
  
  // Before today's cutoff the current window started yesterday; after it, today
  return calendar.windowsFrom(calendar.beforeTodaysCutoff ? -1 : 0, 1);
}

// Days since the most recent cutoff on `resetDay`
function daysSinceReset(calendar: ReturnType<typeof orgCalendar>, resetDay: number): number {
  const days = (calendar.dayOfWeek - resetDay + 7) % 7;
  
  // If today is reset day but before cutoff, we're still in the previous window
  return days === 0 && calendar.beforeTodaysCutoff ? 7 : days;
}

// Weekly windows: each window is 7 calendar days, resets on the day specified in customDays
function calculateWeeklyWindows(config: OrderWindowConfig, now: Date): OrderWindows {
  // The window resets on the delivery day (Monday if none is set)
  const resetDay = weeklyDeliveryDay(config.customDays);

  const calendar = orgCalendar(config, now);
  return calendar.windowsFrom(-daysSinceReset(calendar, resetDay), 7);
}

// Biweekly windows: each window is 14 calendar days, resets on the day specified in customDays
function calculateBiweeklyWindows(config: OrderWindowConfig, now: Date): OrderWindows {
  // The window resets on the delivery day (Monday if none is set)
  const resetDay = weeklyDeliveryDay(config.customDays);
  
  const calendar = orgCalendar(config, now);
  const mostRecentReset = -daysSinceReset(calendar, resetDay);
  
  // Use anchorStart to determine which week in the 2-week cycle we're in:
  // count whole weeks from the first reset day on or after the anchor's
  // calendar date in the org's zone
  const anchorStart = new Date(config.anchorStart);
  let weeksSinceAnchor = 0;
  if (!isNaN(anchorStart.getTime())) {
    const anchor = wallClockInTimezone(anchorStart, config.timezone);
    const anchorDay = Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate())
      + ((resetDay - anchor.getUTCDay() + 7) % 7) * DAY_MS;
    const { today } = calendar;
    const resetDayMs = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + mostRecentReset);
    weeksSinceAnchor = Math.round((resetDayMs - anchorDay) / (7 * DAY_MS));
  }

  // If odd number of weeks since anchor, we're in week 2 of the biweekly cycle
  // and the window started last week
  const isSecondWeek = weeksSinceAnchor % 2 !== 0;
  
  return calendar.windowsFrom(mostRecentReset - (isSecondWeek ? 7 : 0), 14);
}

// Custom windows: windows on specific days of the week
function calculateCustomWindows(config: OrderWindowConfig, now: Date): OrderWindows {
  const customDays = getCustomDaysFromBitmask(config.customDays);
  
  if (customDays.length === 0) {
    // Fallback to weekly if no custom days set
    return calculateWeeklyWindows(config, now);
  }
  
  const calendar = orgCalendar(config, now);
  const isCustomDay = (dayOffset: number) => customDays.includes((((calendar.dayOfWeek + dayOffset) % 7) + 7) % 7);
  
  // The nearest custom day strictly after / before a day. customDays is not
  // empty, so one is always found within a week.
  const nextCustomDay = (dayOffset: number) => {
    for (let offset = dayOffset + 1; offset < dayOffset + 7; offset++) {
      if (isCustomDay(offset)) return offset;
    }
    return dayOffset + 7;
  };
  const previousCustomDay = (dayOffset: number) => {
    for (let offset = dayOffset - 1; offset > dayOffset - 7; offset--) {
      if (isCustomDay(offset)) return offset;
    }
    return dayOffset - 7;
  };
  
  // The current window ends at the next custom day cutoff that has not passed
  const currentEnd = isCustomDay(0) && calendar.beforeTodaysCutoff ? 0 : nextCustomDay(0);
  const currentStart = previousCustomDay(currentEnd);
  const { cutoffOn } = calendar;
  
  return {
    current: createWindow(cutoffOn(currentStart), cutoffOn(currentEnd), config),
    previous: createWindow(cutoffOn(previousCustomDay(currentStart)), cutoffOn(currentStart), config),
    next: createWindow(cutoffOn(currentEnd), cutoffOn(nextCustomDay(currentEnd)), config),
  };
}

// Create a window object with label
function createWindow(start: Cutoff, end: Cutoff, config: OrderWindowConfig): OrderWindow {
  // Format label based on frequency — the dates are the org's calendar dates
  const startStr = formatWindowDate(start.wall);
  const endStr = formatWindowDate(end.wall);
  
  let label: string;
  if (config.frequency === 'daily') {
    label = endStr; // Just show the end date for daily
  } else {
    label = `${startStr} - ${endStr}`;
  }
  
  return {
    startTime: new Date(start.instant),
    endTime: new Date(end.instant),
    cutoffTime: new Date(end.instant),
    label,
  };
}

// Format a wall-clock date for window display
function formatWindowDate(wall: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[wall.getUTCMonth()]} ${wall.getUTCDate()}`;
}

// Check which window an order belongs to based on its creation time
export function getOrderWindow(orderDate: Date, config: OrderWindowConfig): 'current' | 'previous' | 'older' {
  const windows = calculateOrderWindows(config);
  const orderTime = orderDate.getTime();
  
  if (orderTime >= windows.current.startTime.getTime() && orderTime < windows.current.endTime.getTime()) {
    return 'current';
  }
  if (orderTime >= windows.previous.startTime.getTime() && orderTime < windows.previous.endTime.getTime()) {
    return 'previous';
  }
  return 'older';
}

// Get window boundaries for config-aware period filtering
// This is the main function for backend to filter orders by period when windows are enabled
export function getWindowBoundaries(config: OrderWindowConfig, period: 'current' | 'previous' | 'all'): { start: Date | null; end: Date | null } {
  if (!config.enabled || period === 'all') {
    return { start: null, end: null };
  }
  
  const windows = calculateOrderWindows(config);
  
  if (period === 'current') {
    return { start: windows.current.startTime, end: windows.current.endTime };
  }
  
  if (period === 'previous') {
    return { start: windows.previous.startTime, end: windows.previous.endTime };
  }
  
  return { start: null, end: null };
}

// Filter orders by window - returns true if order falls within specified window
export function isOrderInWindow(orderDate: Date, config: OrderWindowConfig, period: 'current' | 'previous'): boolean {
  if (!config.enabled) {
    return true; // When windows disabled, all orders match
  }
  
  const orderWindow = getOrderWindow(orderDate, config);
  return orderWindow === period;
}

// Create a config from database record for use in period calculations
export function createWindowConfigFromDbRecord(record: {
  enabled: boolean;
  frequency: string;
  timezone: string;
  anchorStart: Date;
  cutoffHour: number;
  cutoffMinute: number;
  customDays: number;
} | null): OrderWindowConfig {
  if (!record) {
    // Default disabled config
    return {
      enabled: false,
      frequency: 'weekly',
      timezone: 'America/Toronto',
      anchorStart: new Date(),
      cutoffHour: 17,
      cutoffMinute: 0,
      customDays: 0,
    };
  }
  
  return {
    enabled: record.enabled,
    frequency: record.frequency as 'daily' | 'weekly' | 'biweekly' | 'custom',
    timezone: record.timezone,
    anchorStart: new Date(record.anchorStart),
    cutoffHour: record.cutoffHour,
    cutoffMinute: record.cutoffMinute,
    customDays: record.customDays,
  };
}

export interface FulfillmentConfig {
  timezone?: string;
  cutoffHour?: number;
  cutoffMinute?: number;
  deliveryLeadDays?: number;
  pickupPrepDays?: number;
  pickupCutoffHour?: number;
  pickupCutoffMinute?: number;
  pickupFollowsDeliverySchedule?: boolean;
  deliveryEnabled?: boolean;
  pickupEnabled?: boolean;
}

export function getEditDeadlineForOrder(
  fulfillmentDateStr: string,
  fulfillmentType: 'delivery' | 'pickup' | null,
  config: FulfillmentConfig
): Date | null {
  if (!fulfillmentDateStr) return null;

  const [year, month, day] = fulfillmentDateStr.split('-').map(Number);
  if (!year || !month || !day) return null;

  const type = fulfillmentType || 'delivery';

  let cutoffOffset: number;
  let cutoffHour: number;
  let cutoffMinute: number;

  if (type === 'delivery' || (type === 'pickup' && config.pickupFollowsDeliverySchedule)) {
    cutoffOffset = config.deliveryLeadDays ?? 1;
    cutoffHour = config.cutoffHour ?? 17;
    cutoffMinute = config.cutoffMinute ?? 0;
  } else {
    cutoffOffset = config.pickupPrepDays ?? 0;
    cutoffHour = config.pickupCutoffHour ?? 17;
    cutoffMinute = config.pickupCutoffMinute ?? 0;
  }

  // The cutoff on the org's wall clock, `cutoffOffset` days before fulfillment,
  // as the instant that clock shows it
  const tz = config.timezone || 'America/Toronto';
  const cutoffWall = new Date(Date.UTC(year, month - 1, day - cutoffOffset, cutoffHour, cutoffMinute, 0, 0));

  return wallClockToInstant(cutoffWall, tz);
}

export function isOrderStillEditable(
  fulfillmentDateStr: string | null | undefined,
  fulfillmentType: string | null | undefined,
  config: FulfillmentConfig,
  status?: string,
  pickupDate?: string | null
): boolean {
  if (status === 'cancelled') return false;

  if (!fulfillmentDateStr) {
    return false;
  }

  let effectiveType: 'delivery' | 'pickup' = 'delivery';
  if (fulfillmentType === 'pickup' || fulfillmentType === 'delivery') {
    effectiveType = fulfillmentType;
  } else if (pickupDate) {
    effectiveType = 'pickup';
  }

  const deadline = getEditDeadlineForOrder(
    fulfillmentDateStr,
    effectiveType,
    config
  );
  if (!deadline) return false;

  return Date.now() < deadline.getTime();
}

// The dashboard's date range filters. Every range below is bounded by midnights on
// the org's calendar in `timezone` — never the process's, which in production is UTC.
export type DateRangeFilter = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'all';

// The dashboard's Today / Week / Month / Quarter / Year keep orders created since
// midnight on the org's calendar: today, seven days ago, this day a month ago (a
// day the month lacks rolls over, as JS dates do: 31 October less a month is
// 1 October), or the first day of this calendar quarter / year — what the
// selector's "This Quarter" / "This Year" say. They apply with order windows on or
// off; only Current / Previous depend on the windows. Null for any other period.
// `storage.filterItemsByPeriodWithConfig` and `resolveOrderPeriodFilter` both
// answer from this, which is what keeps the two in step.
export function dateRangeFilterStart(period: string, timezone: string): Date | null {
  switch (period) {
    case 'today':
    case 'week':
    case 'month':
    case 'quarter':
    case 'year':
      return getDateRangeForFilter(period, timezone).start;
    default:
      return null;
  }
}

// With windows disabled the dashboard labels 'current' / 'previous' This Week /
// Last Week: Monday to Sunday on the org's calendar, both ends inclusive — the
// bounds `resolveTimeframeRange` gives them, which GET /orders?period= filters
// on. Null for any other period. With windows ENABLED they
// are the order windows instead, and this does not apply.
// `storage.filterItemsByPeriodWithConfig` and `resolveOrderPeriodFilter` both
// answer from this, which is what keeps the two in step.
export function windowsOffWeekRange(period: string, timezone: string): { start: Date; end: Date } | null {
  if (period !== 'current' && period !== 'previous') return null;
  const { start, end } = resolveTimeframeRange(period, timezone);
  return start && end ? { start, end } : null;
}

// A custom range, picked as two calendar days, as the instants that open the first
// day and close the last one on the org's calendar; both ends inclusive. A bare
// YYYY-MM-DD names a day on that calendar. A full timestamp is an instant already:
// as the start it is kept as given, and as the end it closes the org's day it falls
// on. Null when either side is not a date.
export function resolveCustomDateRange(
  range: { start: string; end: string },
  timezone: string
): { start: Date; end: Date } | null {
  const BARE_DATE = /^\d{4}-\d{2}-\d{2}$/;
  // A bare date parses as midnight UTC, so its UTC fields are the picked day.
  const start = new Date(range.start);
  const end = new Date(range.end);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const lastDay = BARE_DATE.test(range.end) ? end : wallClockInTimezone(end, timezone);
  return {
    start: BARE_DATE.test(range.start) ? wallClockToInstant(start, timezone) : start,
    end: wallClockToInstant(
      new Date(Date.UTC(lastDay.getUTCFullYear(), lastDay.getUTCMonth(), lastDay.getUTCDate(), 23, 59, 59, 999)),
      timezone
    ),
  };
}

export function getDateRangeForFilter(filter: DateRangeFilter, timezone: string = 'America/Toronto'): { start: Date | null; end: Date | null } {
  const now = new Date();
  
  // Midnight on the zone's calendar, some months and days back from today
  const midnight = (monthsBack: number, daysBack: number) => {
    const today = wallClockInTimezone(now, timezone);
    const wall = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - monthsBack, today.getUTCDate() - daysBack));
    return wallClockToInstant(wall, timezone);
  };
  
  switch (filter) {
    case 'today':
      return { start: midnight(0, 0), end: now };
    case 'week':
      return { start: midnight(0, 7), end: now };
    case 'month':
      return { start: midnight(1, 0), end: now };
    // The calendar quarter and year, as GET /orders?period= has always meant them.
    case 'quarter':
    case 'year':
      return { start: resolveTimeframeRange(filter, timezone).start, end: now };
    case 'all':
    default:
      return { start: null, end: null };
  }
}

// Get human-readable description of window config
export function getWindowConfigDescription(config: OrderWindowConfig): string {
  if (!config.enabled) {
    return 'Order windows disabled';
  }
  
  const cutoffStr = formatCutoffTime(config.cutoffHour, config.cutoffMinute);
  
  switch (config.frequency) {
    case 'daily':
      return `Daily windows, cutoff at ${cutoffStr}`;
    case 'weekly':
      return `Weekly windows, cutoff at ${cutoffStr}`;
    case 'biweekly':
      return `Bi-weekly windows, cutoff at ${cutoffStr}`;
    case 'custom': {
      const days = getCustomDaysFromBitmask(config.customDays);
      const dayNames = days.map(d => getDayName(d)).join(', ');
      return `${dayNames} windows, cutoff at ${cutoffStr}`;
    }
    default:
      return 'Unknown configuration';
  }
}

// ==========================================
// END: Configurable Order Window System
// ==========================================

// Resolve a timeframe period to a date range for filtering: real instants, bounded
// by midnights on the org's calendar in `timezone` (the org's window config carries
// it, windows enabled or not). Both ends inclusive.
//
// 'current' / 'previous' are what the dashboard labels them with windows DISABLED:
// This Week / Last Week. With windows enabled they are the order windows — use
// `getWindowBoundaries`, which needs the whole config, not just the zone.
//
// A QuickBooks TxnDate is a calendar date with no zone: compare it with
// `wallClockInTimezone` / `calendarDateInTimezone` of these bounds, not with the
// instants themselves.
export function resolveTimeframeRange(period: string, timezone: string): { start: Date | null, end: Date | null } {
  const now = new Date();
  const today = wallClockInTimezone(now, timezone);
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const date = today.getUTCDate();

  // The first and last instants of a day on the org's calendar; out-of-range
  // months and days roll over as they do in Date.UTC.
  const startOfDay = (y: number, m: number, d: number) =>
    wallClockToInstant(new Date(Date.UTC(y, m, d)), timezone);
  const endOfDay = (y: number, m: number, d: number) =>
    wallClockToInstant(new Date(Date.UTC(y, m, d, 23, 59, 59, 999)), timezone);

  // Weeks run Monday to Sunday.
  const monday = date - (today.getUTCDay() + 6) % 7;

  switch (period) {
    case 'today':
      return { start: startOfDay(year, month, date), end: endOfDay(year, month, date) };

    case 'week':
    case 'current':
      return { start: startOfDay(year, month, monday), end: endOfDay(year, month, monday + 6) };

    case 'previousweek':
    case 'previous':
      return { start: startOfDay(year, month, monday - 7), end: endOfDay(year, month, monday - 1) };

    case 'month':
      return { start: startOfDay(year, month, 1), end: endOfDay(year, month + 1, 0) };

    // Quarter and Year run to now.
    case 'quarter':
      return { start: startOfDay(year, Math.floor(month / 3) * 3, 1), end: now };

    case 'year':
      return { start: startOfDay(year, 0, 1), end: now };

    case 'alltime':
    default:
      // No filtering - include all orders
      return { start: null, end: null };
  }
}